import { useState } from 'react';
import { api } from '../../lib/api';
import { hasRole } from '../../lib/auth';
import { T, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { Link, navigate, useLocation } from '../../lib/router';
import { AsyncButton, Badge, Button, Card, DataTable, Empty, ErrorBox, Field, Input, Loading, Modal, PageHeader, Select, Stat, Textarea, useToast } from '../../ui/components';

export function AddLeadModal({ onClose, onSaved }: { onClose(): void; onSaved?: (personId: string) => void }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const [v, setV] = useState({ firstName: '', lastName: '', email: '', phone: '', source: 'walk_in', message: '', locationId: gym.locations[0]?.id ?? '' });
  return (
    <Modal title={i18n.t('crm.add_lead')} onClose={onClose} footer={
      <AsyncButton disabled={!v.firstName || (!v.email && !v.phone)} onClick={async () => {
        const r = await api(`${T(gym.slug)}/admin/leads`, { body: v });
        toast.ok(i18n.t('crm.lead_added'));
        onSaved?.(r.person?.id ?? r.personId);
        onClose();
      }}>{i18n.t('common.save')}</AsyncButton>
    }>
      <div className="grid-2">
        <Field label={`${i18n.field('first_name')} *`}><Input value={v.firstName} onChange={(e) => setV({ ...v, firstName: e.target.value })} /></Field>
        <Field label={i18n.field('last_name')}><Input value={v.lastName} onChange={(e) => setV({ ...v, lastName: e.target.value })} /></Field>
        <Field label={i18n.field('email')}><Input type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} /></Field>
        <Field label={i18n.field('phone')}><Input type="tel" value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} /></Field>
        <Field label={i18n.field('source')}><Select value={v.source} onChange={(e) => setV({ ...v, source: e.target.value })} options={['walk_in', 'phone', 'referral', 'website', 'event', 'other'].map((s) => ({ value: s, label: i18n.option(s) }))} /></Field>
        {gym.locations.length > 1 && <Field label={i18n.field('location_id')}><Select value={v.locationId} onChange={(e) => setV({ ...v, locationId: e.target.value })} options={gym.locations.map((l) => ({ value: l.id, label: l.name }))} /></Field>}
      </div>
      <Field label={i18n.t('public.message')}><Textarea rows={3} value={v.message} onChange={(e) => setV({ ...v, message: e.target.value })} /></Field>
    </Modal>
  );
}

function Checklist() {
  const gym = useGym();
  const i18n = useI18n();
  const a = `${gym.base}/admin`;
  const steps = [
    { key: 'payments', to: `${a}/integrations`, done: gym.gym.paymentsEnabled },
    { key: 'locations', to: `${a}/locations`, done: gym.locations.some((l) => l.opening_hours && Object.keys(l.opening_hours).length) },
    { key: 'lessons', to: `${a}/lessons`, done: gym.lessonTypes.length > 0 },
    { key: 'schedule', to: `${a}/planner`, done: false },
    { key: 'trainers', to: `${a}/trainers`, done: false },
    { key: 'plans', to: `${a}/memberships`, done: false },
    { key: 'website', to: `${a}/settings?tab=website`, done: false },
  ];
  return (
    <Card title={i18n.t('dashboard.getting_started')}>
      <ol className="checklist">
        {steps.map((s) => <li key={s.key} className={s.done ? 'done' : ''}><Link to={s.to}>{i18n.t(`dashboard.step_${s.key}`)}</Link></li>)}
      </ol>
    </Card>
  );
}

export function DashboardPage() {
  const onboarding = useApi(() => (hasRole(gym.membership, 'owner', 'manager') ? api<any>(`${T(gym.slug)}/admin/welcome`) : Promise.resolve(null)), []);
  const legal = useApi(() => (hasRole(gym.membership, 'owner') ? api<any>(`${T(gym.slug)}/admin/legal/pending`) : Promise.resolve(null)), []);
  const gym = useGym();
  const i18n = useI18n();
  const { query } = useLocation();
  const d = useApi(() => api(`${T(gym.slug)}/admin/dashboard`), []);
  const [lead, setLead] = useState(false);
  const roles = gym.membership?.roles ?? [];
  const setup = useApi(() => api<any>(`${T(gym.slug)}/admin/setup`), []);
  if (d.error) return <div className="page"><ErrorBox error={d.error} onRetry={d.reload} /></div>;
  if (!d.data) return <Loading />;
  const { kpis, today, alerts, tasks } = d.data;
  const a = `${gym.base}/admin`;
  return (
    <div className="page">
      {onboarding.data?.active && !onboarding.data.state.finishedAt && (
        <Card className="onb-banner">
          <div className="row between wrap">
            <div>
              <strong>{i18n.t('onboard.continue_setup')}</strong>
              <p className="small muted">{i18n.t('onboard.continue_setup_text', {
                done: onboarding.data.state.done.length + onboarding.data.state.skipped.length, total: onboarding.data.steps.length,
                step: i18n.t(`onboard.step_${onboarding.data.state.step === 'done' ? 'website' : onboarding.data.state.step}`),
              })}</p>
            </div>
            <Button onClick={() => navigate(`${gym.base}/admin/welcome`)}>{i18n.t('onboard.continue_setup')}</Button>
          </div>
        </Card>
      )}
      {legal.data && legal.data.pending.length > 0 && (
        <Card title={i18n.t('legal.update_title')} className="risk risk-medium">
          <p className="small">{i18n.t('legal.update_text')}</p>
          <ul className="sets">
            {legal.data.pending.map((d: any) => (
              <li key={d.document}><a href={`/legal/${d.document}`} target="_blank" rel="noreferrer">{i18n.t(`legal.doc_${d.document}`)}</a> ({d.version})</li>
            ))}
          </ul>
          <AsyncButton onClick={async () => { await api(`${T(gym.slug)}/admin/legal/accept`, { body: { language: i18n.locale } }); legal.reload(); }}>
            {i18n.t('legal.accept')}
          </AsyncButton>
        </Card>
      )}
      {setup.data && !setup.data.complete && !(onboarding.data?.active && !onboarding.data.state.finishedAt) && (
        <Card title={i18n.t('setup.title')} actions={<span className="small muted">{setup.data.done}/{setup.data.total}</span>}>
          <p className="small muted">{i18n.t('setup.subtitle')}</p>
          <ul className="sets">
            {setup.data.steps.map((s2: any) => (
              <li key={s2.key}>
                {s2.done ? '✓' : '○'}{' '}
                {s2.done ? i18n.t(`setup.step_${s2.key}`) : <a href={s2.link}>{i18n.t(`setup.step_${s2.key}`)}</a>}
              </li>
            ))}
          </ul>
        </Card>
      )}
      <PageHeader title={i18n.t('dashboard.title')} subtitle={i18n.day(new Date().toISOString().slice(0, 10), { weekday: 'long', day: 'numeric', month: 'long' })}
        actions={<>{hasRole(gym.membership, 'owner', 'manager', 'desk') && <Button variant="secondary" onClick={() => setLead(true)}>+ {i18n.t('crm.add_lead')}</Button>}<Button onClick={() => navigate(`${a}/planner`)}>{i18n.t('nav.planner')}</Button></>} />
      {(query.get('welcome') || !gym.gym.paymentsEnabled) && roles.includes('owner') && <Checklist />}
      <div className="stats">
        <Stat label={i18n.t('dashboard.active_members')} value={i18n.num(kpis.activeMembers)} />
        <Stat label={i18n.t('dashboard.new_leads')} value={i18n.num(kpis.newLeads7d)} />
        <Stat label={i18n.t('dashboard.occupancy')} value={kpis.occupancy7d === null ? '—' : `${kpis.occupancy7d}%`} />
        <Stat label={i18n.t('dashboard.no_trainer')} value={kpis.openSessions7d} tone={kpis.openSessions7d ? 'warn' : undefined} />
        <Stat label={i18n.t('dashboard.open_tasks')} value={kpis.openTasks} />
        <Stat label={i18n.t('dashboard.failed_payments')} value={kpis.failedPayments} tone={kpis.failedPayments ? 'bad' : undefined} />
      </div>
      <div className="grid-2 align-start">
        <Card title={i18n.t('dashboard.today', { booked: today.booked, capacity: today.capacity })}>
          <DataTable rows={today.sessions} onRowClick={() => navigate(`${a}/planner`)} columns={[
            { key: 'starts_at', label: i18n.t('planner.when'), render: (s) => i18n.time(s.starts_at) },
            { key: 'lesson_name', label: i18n.t('planner.lesson') },
            { key: 'trainer_name', label: i18n.t('planner.trainer'), render: (s) => s.trainer_name ?? <span className="warn">{i18n.t('adminplanner.no_trainer')}</span> },
            { key: 'booked', label: i18n.t('planner.attendees'), render: (s) => `${s.booked_count}/${s.capacity}` },
            { key: 'status', label: '', render: (s) => (s.status !== 'scheduled' ? <Badge value={s.status} /> : null) },
          ]} empty={i18n.t('dashboard.no_lessons_today')} />
        </Card>
        <div className="stack">
          <Card title={i18n.t('dashboard.alerts')}>
            {alerts.length === 0 ? <Empty icon="✓">{i18n.t('dashboard.no_alerts')}</Empty> : (
              <div className="stack-sm">
                {alerts.map((al: any) => (
                  <div key={al.id} className={`alert-row sev-${al.severity}`}>
                    <div>
                      <strong>{al.title}</strong>
                      {al.body && <div className="small">{al.body}</div>}
                      <div className="small muted">{i18n.dateTime(al.created_at)}{al.person_id && <> · <Link to={`${a}/clients/${al.person_id}`}>{al.person_name}</Link></>}</div>
                    </div>
                    <AsyncButton size="sm" variant="ghost" onClick={async () => { await api(`${T(gym.slug)}/admin/alerts/${al.id}/ack`, { body: {} }); d.reload(); }}>{i18n.t('dashboard.ack')}</AsyncButton>
                  </div>
                ))}
              </div>
            )}
          </Card>
          <Card title={i18n.t('nav.tasks')} actions={<Link to={`${a}/tasks`}>{i18n.t('common.all')}</Link>}>
            {tasks.length === 0 ? <Empty icon="✓">{i18n.t('dashboard.no_tasks')}</Empty> : (
              <div className="stack-sm">
                {tasks.map((t: any) => (
                  <div key={t.id} className="row between">
                    <div>
                      <span className={t.due_at && t.due_at < new Date().toISOString() ? 'warn' : ''}>{t.title}</span>
                      <div className="small muted">{t.due_at ? i18n.dateTime(t.due_at) : ''}{t.person_id && <> · <Link to={`${a}/clients/${t.person_id}`}>{t.person_name}</Link></>}</div>
                    </div>
                    <AsyncButton size="sm" variant="secondary" onClick={async () => { await api(`${T(gym.slug)}/admin/r/tasks/${t.id}`, { method: 'PATCH', body: { status: 'done' } }); d.reload(); }}>✓</AsyncButton>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
      {lead && <AddLeadModal onClose={() => setLead(false)} onSaved={d.reload} />}
    </div>
  );
}
